import type { StoredGuardPolicy } from '@nadoutong/contracts';
import { useT } from '../../../i18n/index.ts';
import { Chip } from '../../../ui/index.ts';
import { skuSummaryChips, type GuardSummaryChip } from './guardModel.ts';
import { signalView } from './guardSignals.ts';

function chipLabel(chip: GuardSummaryChip, t: ReturnType<typeof useT>): string {
  const signal = t(signalView(chip.kind).short);
  const base = chip.part === 'trip'
    ? t('guaranteed.guard.chip.trip', { signal })
    : chip.seconds === null
      ? t('guaranteed.guard.chip.walk', { signal })
      : t('guaranteed.guard.chip.walk_seconds', { signal, seconds: String(chip.seconds) });
  return chip.mode === 'shadow' ? t('guaranteed.guard.chip.shadow', { label: base }) : base;
}

export function GuardConfigCell({
  skuId,
  skuPolicy,
  legacyStreamCutoffMs,
  overlayCount,
  disabled,
  onOpen,
}: {
  readonly skuId: string;
  readonly skuPolicy: StoredGuardPolicy;
  readonly legacyStreamCutoffMs: number | null;
  readonly overlayCount: number;
  readonly disabled: boolean;
  readonly onOpen: () => void;
}): JSX.Element {
  const t = useT();
  const chips = skuSummaryChips(skuPolicy, legacyStreamCutoffMs);
  return (
    <button
      type="button"
      className="flex min-w-0 flex-col items-start gap-1 rounded px-1 py-0.5 text-left hover:bg-raised disabled:pointer-events-none"
      disabled={disabled}
      title={t('guaranteed.guard.open')}
      data-testid={`guaranteed-guard-open-${skuId}`}
      onClick={onOpen}
    >
      {chips.length === 0 ? (
        <span className="text-dense text-paper-low">{t('guaranteed.guard.off')}</span>
      ) : (
        <span className="flex flex-wrap gap-1">
          {chips.map((chip) => (
            <Chip
              key={`${chip.kind}-${chip.part}`}
              tone={chip.mode === 'shadow' ? 'warn' : 'signal'}
              lamp={false}
              testId={`guaranteed-guard-chip-${skuId}-${chip.kind}-${chip.part}`}
            >
              {chipLabel(chip, t)}
            </Chip>
          ))}
        </span>
      )}
      {overlayCount > 0 ? (
        <span className="text-micro text-paper-low" data-testid={`guaranteed-guard-overlays-${skuId}`}>
          {t('guaranteed.guard.overlay_count', { n: String(overlayCount) })}
        </span>
      ) : null}
    </button>
  );
}
